import { useState, useEffect } from 'react';
import { Button } from '@acb_devel/design-system';
import {
  PopoverRoot,
  PopoverTrigger,
  PopoverContent,
  PopoverClose,
} from '@acb_devel/design-system';
import { FilterButton } from '@components/assessors/atoms/FilterButton/FilterButton';
import { Field } from '@acb_devel/design-system';
import { ScrollArea } from '@acb_devel/design-system';
import { Radio, RadioItem } from '@acb_devel/design-system';
import {
  AccordionRoot,
  AccordionItem,
  AccordionHeader,
  AccordionButton,
  AccordionContent,
} from '@acb_devel/design-system';
import { AngleDownIcon } from '@acb_devel/design-system';
import { SearchIcon } from '@acb_devel/design-system';
import type { Event } from '@services/events';
import { getEvents } from '@services/events';
import { Skeleton } from '@acb_devel/design-system';
import { useEventsContext } from '@contexts/EventsContext';

import styles from './EventFilter.module.scss';

interface EventTypeGroup {
  eventTypeId: number;
  name: string;
  events: Event[];
}

export function EventFilter({
  event,
  setEvent,
}: {
  event: Event | null;
  setEvent: (event: Event | null) => void;
}) {
  const { events } = useEventsContext();

  const [search, setSearch] = useState('');
  const [eventsTypes, setEventsTypes] = useState<EventTypeGroup[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<string | undefined>(
    event?.id?.toString()
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getEvents({
      ordering: '-start_event',
    })
      .then(({ results }) => {
        const eventsGroups: EventTypeGroup[] = [];

        // group events by type
        for (const event of results) {
          const eventTypeGroup = eventsGroups.find(
            (group) => group.eventTypeId === event.eventType.id
          );

          if (eventTypeGroup) {
            eventTypeGroup.events.push(event);
          } else {
            eventsGroups.push({
              eventTypeId: event.eventType.id,
              name: event.eventType.name,
              events: [event],
            });
          }
        }

        setEventsTypes(eventsGroups);
      })
      .finally(() => setLoading(false));
  }, [events]);

  function setDefaultEvent() {
    if (loading) {
      return;
    }

    if (!event && !eventsTypes.length) {
      return;
    }

    if (!event) {
      setEvent(eventsTypes[0].events[0]);
      setSelectedEvent(eventsTypes[0].events[0].id.toString());
      return;
    }

    if (!eventsTypes.length) {
      setSelectedEvent(event.id.toString());
      return;
    }

    const selectedEventObj = eventsTypes
      .map((eventType) => eventType.events)
      .flat()
      .find((loopEvent) => loopEvent.id.toString() === event.id.toString());

    if (!selectedEventObj) {
      setEvent(eventsTypes[0].events[0]);
      return;
    }

    setSelectedEvent(selectedEventObj.id.toString());
  }

  useEffect(() => {
    setDefaultEvent();
  }, [event, eventsTypes]);

  function handleApply() {
    const selectedEventObj = eventsTypes
      .map((eventType) => eventType.events)
      .flat()
      .find((event) => event.id.toString() === selectedEvent);

    if (selectedEventObj) {
      setEvent(selectedEventObj);
    }
  }

  return (
    <PopoverRoot>
      <PopoverTrigger>
        <FilterButton>
          <Skeleton loading={loading}>
            <strong>Evento</strong>
            {event && `: ${event.fullName}`}
          </Skeleton>
        </FilterButton>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={10}
        onOpenAutoFocus={() => setDefaultEvent()}
        paddingOff
      >
        <div className={styles.eventFilter__popoverContent}>
          <div className={styles.eventFilter__search}>
            <Field
              id="search"
              label=""
              placeholder="Buscar por..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              prependSlot={<SearchIcon />}
            />
          </div>
          <ScrollArea className={styles.eventFilter__scrollArea}>
            <div className={styles.eventFilter__eventTypesList}>
              <Radio
                id="event"
                onValueChange={setSelectedEvent}
                value={selectedEvent}
              >
                <AccordionRoot
                  type="single"
                  id={`event_${event?.eventType.id}`}
                >
                  {eventsTypes
                    .filter((eventType) =>
                      eventType.name
                        .toLowerCase()
                        .includes(search.toLowerCase())
                    )
                    .map((eventType) => (
                      <AccordionItem
                        id={`event_${eventType.eventTypeId}`}
                        key={eventType.eventTypeId}
                      >
                        <AccordionHeader>
                          <div className={styles.eventFilter__eventTypeName}>
                            {eventType.name}
                            <AccordionButton>
                              <Button
                                variant="flat"
                                size="md"
                                color="secondary"
                                isOnlyIcon
                              >
                                <AngleDownIcon />
                              </Button>
                            </AccordionButton>
                          </div>
                        </AccordionHeader>
                        <AccordionContent>
                          <div className={styles.eventFilter__eventsList}>
                            {eventType.events.map((event) => (
                              <RadioItem
                                id={`event_${event.id}`}
                                key={event.id}
                                value={event.id.toString()}
                              >
                                {event.location} {event.edition}
                              </RadioItem>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                </AccordionRoot>
              </Radio>
            </div>
          </ScrollArea>
          <div className={styles.eventFilter__actions}>
            {/* <PopoverClose>
              <Button
                variant="flat"
                color="primary"
                size="md"
                onClick={() => {
                  setSelectedEvent('');
                  setEvent(null);
                }}
                upperCase={false}
              >
                Borrar
              </Button>
            </PopoverClose> */}
            <PopoverClose>
              <Button
                variant="solid"
                color="primary"
                size="md"
                onClick={handleApply}
                upperCase={false}
              >
                Aplicar
              </Button>
            </PopoverClose>
          </div>
        </div>
      </PopoverContent>
    </PopoverRoot>
  );
}
